import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { useGoogleAnalytics } from "@/hooks/use-google-analytics";
import { useClarity } from "@/hooks/use-clarity";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CompareBar from "./components/CompareBar";
import StickyMobileCTA from "./components/StickyMobileCTA";

// Lazy load pages that aren't needed on initial homepage load
const Quote = lazy(() => import("./pages/Quote"));
const ForMovers = lazy(() => import("./pages/ForMovers"));
const CompanyRegister = lazy(() => import("./pages/CompanyRegister"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const Contact = lazy(() => import("./pages/Contact"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const Press = lazy(() => import("./pages/Press"));
const Widgets = lazy(() => import("./pages/Widgets"));
const EmbedCalculator = lazy(() => import("./pages/EmbedCalculator"));
const EmbedChecklist = lazy(() => import("./pages/EmbedChecklist"));
const CompanyList = lazy(() => import("./pages/CompanyList"));
const TopCompaniesCity = lazy(() => import("./pages/TopCompaniesCity"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const Verhuischecklist = lazy(() => import("./pages/Verhuischecklist"));
const VolumeCalculator = lazy(() => import("./pages/VolumeCalculator"));
const Verhuiskosten = lazy(() => import("./pages/Verhuiskosten"));
const Verhuistips = lazy(() => import("./pages/Verhuistips"));
const AdreswijzigingDoorgeven = lazy(() => import("./pages/AdreswijzigingDoorgeven"));
const EnergiecontractVerhuizen = lazy(() => import("./pages/EnergiecontractVerhuizen"));
const VerzekeringenAanpassen = lazy(() => import("./pages/VerzekeringenAanpassen"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Compare = lazy(() => import("./pages/Compare"));
const RegistrationSuccess = lazy(() => import("./pages/RegistrationSuccess"));
const BrandAssets = lazy(() => import("./pages/BrandAssets"));
const Verhuisoffertes = lazy(() => import("./pages/Verhuisoffertes"));
const VerhuisoffertesCity = lazy(() => import("./pages/VerhuisoffertesCity"));
const GoedkoopVerhuizen = lazy(() => import("./pages/GoedkoopVerhuizen"));
const PianoVerhuizen = lazy(() => import("./pages/PianoVerhuizen"));
const SeniorenVerhuizen = lazy(() => import("./pages/SeniorenVerhuizen"));
const StudentenVerhuizing = lazy(() => import("./pages/StudentenVerhuizing"));
const AuthorPage = lazy(() => import("./pages/AuthorPage"));
const OverOns = lazy(() => import("./pages/OverOns"));
const KluisVerhuizen = lazy(() => import("./pages/KluisVerhuizen"));
const AquariumVerhuizen = lazy(() => import("./pages/AquariumVerhuizen"));
const KunstVerhuizen = lazy(() => import("./pages/KunstVerhuizen"));
const VerhuizenNaarBelgie = lazy(() => import("./pages/VerhuizenNaarBelgie"));
const VerhuizenNaarDuitsland = lazy(() => import("./pages/VerhuizenNaarDuitsland"));
const MoversBlog = lazy(() => import("./pages/MoversBlog"));
const MoversBlogPost = lazy(() => import("./pages/MoversBlogPost"));
const PrivacyBedrijven = lazy(() => import("./pages/PrivacyBedrijven"));
const TermsBedrijven = lazy(() => import("./pages/TermsBedrijven"));
const MoversContact = lazy(() => import("./pages/MoversContact"));

const queryClient = new QueryClient();

const AppContent = () => {
  useGoogleAnalytics();
  useClarity();
  
  return (
    <>
      <ScrollToTop />
      <ScrollToTopButton />
      <CookieConsent />
      <CompareBar />
      <StickyMobileCTA />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/quote" element={<Quote />} />
          <Route path="/voor-verhuizers" element={<ForMovers />} />
          <Route path="/voor-verhuizers/blog" element={<MoversBlog />} />
          <Route path="/voor-verhuizers/blog/:slug" element={<MoversBlogPost />} />
          <Route path="/voor-verhuizers/contact" element={<MoversContact />} />
          <Route path="/for-movers" element={<Navigate to="/voor-verhuizers" replace />} />
          <Route path="/verhuizer-aanmelden" element={<CompanyRegister />} />
          <Route path="/aanmelding-verzonden" element={<RegistrationSuccess />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/auteur/:slug" element={<AuthorPage />} />
          <Route path="/verhuizen/:city" element={<CityLanding />} />
          <Route path="/verhuisbedrijven" element={<CompanyList />} />
          <Route path="/beste-verhuisbedrijven/:city" element={<TopCompaniesCity />} />
          <Route path="/verhuisbedrijf/:slug" element={<CompanyProfile />} />
          <Route path="/verhuischecklist" element={<Verhuischecklist />} />
          <Route path="/volume-calculator" element={<VolumeCalculator />} />
          <Route path="/verhuiskosten" element={<Verhuiskosten />} />
          <Route path="/verhuistips" element={<Verhuistips />} />
          <Route path="/adreswijziging-doorgeven" element={<AdreswijzigingDoorgeven />} />
          <Route path="/energiecontract-verhuizen" element={<EnergiecontractVerhuizen />} />
          <Route path="/verzekeringen-aanpassen" element={<VerzekeringenAanpassen />} />
          <Route path="/favorieten" element={<Favorites />} />
          <Route path="/vergelijken" element={<Compare />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/voorwaarden" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/privacy-bedrijven" element={<PrivacyBedrijven />} />
          <Route path="/voorwaarden-bedrijven" element={<TermsBedrijven />} />
          <Route path="/affiliate" element={<Affiliate />} />
          <Route path="/pers" element={<Press />} />
          <Route path="/widgets" element={<Widgets />} />
          <Route path="/embed/calculator" element={<EmbedCalculator />} />
          <Route path="/embed/checklist" element={<EmbedChecklist />} />
          <Route path="/brand-assets" element={<BrandAssets />} />
           <Route path="/verhuisoffertes" element={<Verhuisoffertes />} />
          <Route path="/verhuisoffertes/:city" element={<VerhuisoffertesCity />} />
          <Route path="/goedkoop-verhuizen" element={<GoedkoopVerhuizen />} />
          <Route path="/piano-verhuizen" element={<PianoVerhuizen />} />
          <Route path="/senioren-verhuizen" element={<SeniorenVerhuizen />} />
          <Route path="/studentenverhuizing" element={<StudentenVerhuizing />} />
          <Route path="/over-ons" element={<OverOns />} />
          <Route path="/kluis-verhuizen" element={<KluisVerhuizen />} />
          <Route path="/aquarium-verhuizen" element={<AquariumVerhuizen />} />
          <Route path="/kunst-verhuizen" element={<KunstVerhuizen />} />
          <Route path="/verhuizen-naar-belgie" element={<VerhuizenNaarBelgie />} />
          <Route path="/verhuizen-naar-duitsland" element={<VerhuizenNaarDuitsland />} />
          {/* Redirect orphan blog slugs to correct blog paths */}
          <Route path="/wie-is-aansprakelijk-bij-verhuisschade" element={<Navigate to="/blog/wie-is-aansprakelijk-bij-verhuisschade" replace />} />
          <Route path="/offerte" element={<Navigate to="/quote" replace />} />
          {/* Redirect old city slugs to new SEO-friendly slugs */}
          <Route path="/verhuizen/denhaag" element={<Navigate to="/verhuizen/den-haag" replace />} />
          <Route path="/verhuizen/denbosch" element={<Navigate to="/verhuizen/den-bosch" replace />} />
          <Route path="/verhuizen/sittardgeleen" element={<Navigate to="/verhuizen/sittard-geleen" replace />} />
          <Route path="/verhuizen/alphenrijn" element={<Navigate to="/verhuizen/alphen-aan-den-rijn" replace />} />
          <Route path="/verhuizen/capelleijssel" element={<Navigate to="/verhuizen/capelle-aan-den-ijssel" replace />} />
          <Route path="/beste-verhuisbedrijven/denhaag" element={<Navigate to="/beste-verhuisbedrijven/den-haag" replace />} />
          <Route path="/beste-verhuisbedrijven/denbosch" element={<Navigate to="/beste-verhuisbedrijven/den-bosch" replace />} />
          <Route path="/beste-verhuisbedrijven/sittardgeleen" element={<Navigate to="/beste-verhuisbedrijven/sittard-geleen" replace />} />
          <Route path="/beste-verhuisbedrijven/alphenrijn" element={<Navigate to="/beste-verhuisbedrijven/alphen-aan-den-rijn" replace />} />
          <Route path="/beste-verhuisbedrijven/capelleijssel" element={<Navigate to="/beste-verhuisbedrijven/capelle-aan-den-ijssel" replace />} />
          <Route path="/verhuisoffertes/denhaag" element={<Navigate to="/verhuisoffertes/den-haag" replace />} />
          <Route path="/verhuisoffertes/denbosch" element={<Navigate to="/verhuisoffertes/den-bosch" replace />} />
          <Route path="/verhuisoffertes/sittardgeleen" element={<Navigate to="/verhuisoffertes/sittard-geleen" replace />} />
          <Route path="/verhuisoffertes/alphenrijn" element={<Navigate to="/verhuisoffertes/alphen-aan-den-rijn" replace />} />
          <Route path="/verhuisoffertes/capelleijssel" element={<Navigate to="/verhuisoffertes/capelle-aan-den-ijssel" replace />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
